import React, { useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { forgotPassword } from 'actions/AuthActions'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import noop from 'lib/function/noop'

interface Props {
  user: App.User
  onModeChange?: (nextMode: App.UiAccountModalMode) => void
  dismissable?: boolean
}

function AccountAccessForgetPassword(props: Props) {
  const {
    user,
    onModeChange = noop,
    dismissable,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()

  const onForgotPassword = useCallback(() => {
    dispatch(forgotPassword(user.email!))
  }, [dispatch, user])

  const onBack = useCallback(() => onModeChange('login'), [onModeChange])

  const modalTitle = intl.formatMessage({
    id: 'SR8Gd02Qjj',
    defaultMessage: 'A reset link has been sent to your email',
    description: 'Modal title after password reset link has been sent',
  })

  const expiryText = intl.formatMessage({
    id: 'ueGidpG7Iy',
    defaultMessage: 'The reset link will expire in 24 hours. Please remember to check your spam folder.',
    description: 'Instructions about password reset link expiry and spam folder',
  })

  return (<>
    <ModalHeader
      title={modalTitle}
      onBackButtonClick={onBack}
      dismissible={dismissable}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24}>
          <BodyText variant="large" weight="bold">{user.email}</BodyText>
          <BodyText variant="medium">
            {expiryText}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="OgSFcD/7PG"
              defaultMessage="Don’t see the email? <link>Resend the email</link>."
              description="Help text with link to resend the password reset email"
              values={{
                link: (chunks) => <TextLink onClick={onForgotPassword}>{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
  )
}

export default AccountAccessForgetPassword
